import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData, useSearchParams, useOutletContext, useNavigation } from "@remix-run/react";
import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import prisma from "~/utils/db.server";
import { ProductCard } from "~/components/storefront/ProductCard";
import { resolveMarket } from "~/services/markets/marketDetection.server";
import { getMarketPrices } from "~/services/markets/marketPricing.server";
import { COLOR_HEX, COLOR_FAMILIES, colorFamilyOf, expandColorFamilies, familiesPresentIn, fixImgUrl } from "~/utils/storefront-constants";
import { getProductBaseName, getProductColor } from "~/utils/productSiblings";
import { displayTitle as titleForLocale, renameProductTitle } from "~/utils/productTitle";
import { t } from "~/utils/translations";
import type { Locale } from "~/utils/locale";
import { detectLocale } from "~/utils/locale";
import { formatStorefrontPrice } from "~/utils/money";
import { FilterSidebar, type FilterGroup } from "~/components/storefront/FilterSidebar";
import { SortDropdown } from "~/components/storefront/SortDropdown";

// T2.15: FilterBottomSheet is mobile-only and never rendered on first paint.
// Lazy-load so the ~8KB of bottom-sheet code doesn't ship in the initial
// bundle for desktop users (who account for most /products traffic).
const FilterBottomSheet = React.lazy(() =>
  import("~/components/storefront/FilterBottomSheet").then((m) => ({ default: m.FilterBottomSheet })),
);

export const meta: MetaFunction = ({ data }: any) => {
  // Safe fallback is the production root domain, NOT the dev `test.*` host
  // — pre-cutover that domain leaked into canonical tags any time the
  // loader didn't run (errors, edge cases) which Google would treat as the
  // canonical version.
  const origin = data?.origin || "https://diolo.com";
  const locale = data?.locale === "vi" ? "vi" : "en";
  const title =
    locale === "vi" ? "Cửa hàng — Diolo" : "Shop All — Diolo";
  const description =
    locale === "vi"
      ? "DIOLO 001 — toàn bộ bộ sưu tập: áo polo, áo thun và quần short pickleball nam, thiết kế tại Sài Gòn."
      : "DIOLO 001 — the full collection: men's pickleball polos, tees and shorts, designed in Saigon.";
  // T3.2: canonical strips query params so filter URLs don't generate
  // duplicate index entries for Googlebot.
  // Audit 2026-04-30: added og:locale, og:image, twitter:* and the
  // BreadcrumbList structured-data tag the SEO sweep flagged missing.
  const ogImage = `${origin}/og/diolo-v2.png`;
  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: `${origin}/products` },
    { property: "og:url", content: `${origin}/products` },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:image", content: ogImage },
    { property: "og:locale", content: locale === "en" ? "en_US" : "vi_VN" },
    { property: "og:site_name", content: "Diolo" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: locale === "vi" ? "Trang chủ" : "Home", item: origin },
          { "@type": "ListItem", position: 2, name: locale === "vi" ? "Cửa hàng" : "Shop", item: `${origin}/products` },
        ],
      },
    },
  ];
};

const PAGE_SIZE = 500;

/**
 * Mega-menu "By Type" → keyword whitelist mapping.
 *
 * The Shop dropdown's By Type entries (Tops / Bottoms / Dresses / Sets /
 * Accessories) link to /products?type=<group>. The merchant's actual
 * Product.productType values are merchandise lines (Cozy / Focus /
 * Recharge / Balance / Pickleball / etc.), not garment types — so we
 * can't filter on productType directly. Instead, when ?type=<group> is
 * present, we filter products by a title-keyword whitelist for that
 * group: a product is in "Tops" if its title (or titleEn) matches one
 * of the keywords below.
 *
 * Sets are detected differently — a product is a "Set" if the title
 * contains " Set " or ends in " Set" (the renameProductTitle helper in
 * app/utils/productTitle.ts already inserts " Set" on bare-named SKUs
 * for the lines that need disambiguation).
 *
 * Each entry is matched case-insensitively as a whole word (\b...\b)
 * so "Topaz" doesn't trigger on "top".
 */
// Menswear taxonomy (2026-06-13 rebrand). The storefront nav now links by
// exact productType (?category=…); these keyword groups back the legacy
// ?type=… filter + any free-text matching. Trimmed to menswear pieces — the
// women's "dresses" group and bra/skort/cami/etc. keywords were removed.
const TYPE_GROUP_KEYWORDS: Record<string, RegExp> = {
  tops: /\b(top|tank|tee|t-shirt|polo|shirt|pullover|hoodie|jacket|sweater|crewneck|longsleeve)\b/i,
  bottoms: /\b(short|shorts|pant|pants|jogger|joggers|trouser|trousers|tights|sweatpant|sweatpants)\b/i,
  // Sets are the bare-named SKUs (no piece keyword) — disambiguated by
  // renameProductTitle which inserts " Set" — so the title will literally
  // contain " Set" by the time it reaches the listing.
  sets: /\bset\b/i,
  accessories: /\b(cap|hat|beanie|sock|socks|tumbler|bottle|bag|tote|duffel|headband|wristband|towel)\b/i,
};

// URL activity aliases → actual DB activity values. The admin UI (and the
// DB) uses "court/studio/train/lounge" (admin.products.$id.tsx:1188-1191),
// but Shopify-era URLs + legacy category links use "tennis/yoga/gym".
// Normalise both conventions so old bookmarks + seeded Shopify redirects
// (/collections/yoga → /products?activity=yoga) keep returning products.
// Keys are lowercase.
const ACTIVITY_URL_TO_DB: Record<string, string> = {
  tennis: "court",
  yoga: "studio",
  gym: "train",
  // passthroughs — accept DB values directly too
  court: "court",
  studio: "studio",
  train: "train",
  lounge: "lounge",
  run: "run",
};
const aliasActivity = (v: string) => ACTIVITY_URL_TO_DB[v.toLowerCase()] ?? v;

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  // Multi-value filters (?activity=train&activity=court). Single-value accessors
  // still read the first value for backward compatibility with old bookmarks.
  const activities = url.searchParams.getAll("activity").filter(Boolean).map(aliasActivity);
  const fabrics = url.searchParams.getAll("fabric").filter(Boolean);
  const categories = url.searchParams.getAll("category").filter(Boolean);
  const fits = url.searchParams.getAll("fit").filter(Boolean);
  // The `?color=` param now accepts FAMILY names (Beige, Black, Blue,
  // Brown, …) per the Aritzia-style family grouping. Legacy raw color
  // names (e.g. "Plum") still work — expandColorFamilies is a no-op
  // for unknown values. Downstream WHERE-clause + in-memory filter
  // both consume the expanded list. See COLOR_FAMILIES.
  const colorParams = url.searchParams.getAll("color").filter(Boolean);
  const colors = expandColorFamilies(colorParams);
  const sizes = url.searchParams.getAll("size").filter(Boolean);
  const tag = url.searchParams.get("tag");
  // ?type=<group> from the Shop > By Type mega menu. Mapped to a
  // title-keyword whitelist below (see TYPE_GROUP_KEYWORDS comment).
  const typeGroup = url.searchParams.get("type")?.toLowerCase() || null;
  // Toggle filters
  const newIn = url.searchParams.get("newIn") === "1" || tag === "new";
  const onSale = url.searchParams.get("onSale") === "1";
  // Default sort is `featured` (orderBy: sortPosition asc, createdAt
  // desc). The merchant has curated sortPosition for ~115 products
  // (2026-05-11 sortposition ops endpoint) so the "All" view matches
  // the same merchant-curated order as the homepage "All" tab.
  //
  // Earlier (2026-05-11) the default was flipped to `newest` because
  // sortPosition was 0 for everything and "featured" was visually
  // indistinguishable from "newest" — that's no longer true now that
  // the curated list is applied. Reverted to `featured` 2026-05-12
  // after merchant reported Bumblebee Balance (sp=9999) appearing
  // above Monaco Espresso (sp=3) on /products.
  const sort = url.searchParams.get("sort") || "featured";
  const search = url.searchParams.get("q");
  const page = Math.max(1, parseInt(url.searchParams.get("page") || "1", 10));
  const collectionFilter = url.searchParams.get("collection");
  const priceMin = url.searchParams.get("priceMin");
  const priceMax = url.searchParams.get("priceMax");
  const locale = detectLocale(request.url);

  // Legacy single-value accessors (kept for backward compat further down)
  const activity = activities[0] || null;
  const fabric = fabrics[0] || null;
  const colorFilter = colors[0] || null;
  const sizeFilter = sizes[0] || null;

  const shop = await prisma.shop.findFirst();
  if (!shop) return json({
    products: [], total: 0, displayCount: 0, page: 1, hasMore: false,
    filters: { activities: [], fabrics: [], collections: [], colorFamilies: [], sizes: [], categories: [], fits: [] },
    facetCounts: {} as Record<string, Record<string, number>>,
    categoryStrip: [] as Array<{ label: string; labelVi: string; link: string; img: string | null }>,
    priceFloor: 0,
    priceCeiling: 5000000,
    locale,
  });

  // Build where clause — only show products that have images, a real price,
  // and at least one variant with actual stock. Zero-price and zero-inventory
  // rows (from Sapo imports or deleted products) should never render.
  // A product is renderable if ANY image source is populated —
  // `featuredImageUrl`, `backupImages` (Shopify backfill), or
  // `mediaOverrides` (admin-uploaded). The old `featuredImageUrl` filter
  // would have hidden products whose only images live in the backfill
  // gallery, which is exactly the case we're trying to fix.
  const where: any = {
    shopId: shop.id,
    isRetailEnabled: true,
    status: "ACTIVE",
    OR: [
      { featuredImageUrl: { not: null } },
      { backupImages: { not: null } },
      { mediaOverrides: { not: null } },
    ],
    // Price comes from EITHER product.retailPrice OR the cheapest
    // available variant — ProductCard already falls back to
    // variant.price[0] when retailPrice is null. Filtering by
    // `retailPrice: { gt: 0 }` here was rejecting 38 products that
    // render fine on the homepage (Cozy Jacket, Cozy Pants, the
    // Femme/Smash/Aura/Courtney/Ace/Balance/Monaco/Whisper PIECE
    // products — all priced on variants only). The variant-level
    // `inventoryQuantity > 0` filter below already prevents
    // zero-stock products from showing; that's the meaningful
    // safety net. Audit 2026-05-06.
    variants: {
      some: {
        isAvailable: true,
        inventoryQuantity: { gt: 0 },
        price: { gt: 0 },
      },
    },
  };

  // Multi-value scalar filters — if any values are passed, use `in`.
  if (activities.length === 1) where.activity = activities[0];
  else if (activities.length > 1) where.activity = { in: activities };
  if (fabrics.length === 1) where.fabric = fabrics[0];
  else if (fabrics.length > 1) where.fabric = { in: fabrics };
  if (categories.length === 1) where.productType = categories[0];
  else if (categories.length > 1) where.productType = { in: categories };
  if (fits.length === 1) where.fitGuideTop = fits[0];
  else if (fits.length > 1) where.fitGuideTop = { in: fits };

  if (tag && tag !== "new") {
    // Audit 2026-05-13: accessories are typed via `productType` (capital
    // "Accessories"), not tagged. The Accessories card on the category
    // strip therefore needs to map `?tag=accessories` → `productType =
    // "Accessories"` so the filter actually returns rows. Other tag-based
    // filters (sale, custom merchant tags) still flow through the
    // tags.contains path below.
    if (tag.toLowerCase() === "accessories") {
      where.productType = { equals: "Accessories", mode: "insensitive" };
    } else {
      where.tags = { contains: tag };
    }
  }

  // "New In" filter — date-based default with optional manual tag override.
  // A product counts as "new" if EITHER:
  //   (a) it has the "new" tag (manual curation by merchandiser), OR
  //   (b) it was created within the last `shop.newArrivalDays` days (default 14).
  // This means new products automatically appear in the New In view without
  // needing to be tagged, while still allowing the team to extend a product's
  // "new" lifespan or promote older items by adding the tag.
  if (newIn) {
    const newArrivalsCutoff = new Date();
    newArrivalsCutoff.setDate(newArrivalsCutoff.getDate() - shop.newArrivalDays);
    const newInConditions = [
      { tags: { contains: "new" } },
      { createdAt: { gte: newArrivalsCutoff } },
    ];
    // Preserve any existing top-level OR (e.g. the image-presence filter).
    // Prisma can't have two top-level `OR` keys, so wrap both groups in AND.
    if (where.OR) {
      where.AND = [
        ...(where.AND || []),
        { OR: where.OR },
        { OR: newInConditions },
      ];
      delete where.OR;
    } else {
      where.AND = [...(where.AND || []), { OR: newInConditions }];
    }
  }
  // "On Sale" heuristic: retailComparePrice must exist and be greater than
  // retailPrice. Prisma can't compare two columns in a single `findMany`
  // where-clause, so we apply `retailComparePrice: { not: null }` here and
  // then filter the result set in-memory below.
  if (onSale) {
    where.retailComparePrice = { not: null };
  }

  if (collectionFilter) where.collection = { name: collectionFilter };
  if (search) {
    where.OR = [
      { title: { contains: search, mode: "insensitive" } },
      { tags: { contains: search, mode: "insensitive" } },
      { productType: { contains: search, mode: "insensitive" } },
    ];
  }
  // Price range filter — match products whose retailPrice OR cheapest
  // available variant falls in range. Some products price on
  // retailPrice; the 38 piece-product rows audited 2026-05-06 price
  // on variants only (retailPrice null). Filtering on retailPrice
  // alone hid them from the price filter even though they have
  // valid in-range variant prices.
  if (priceMin || priceMax) {
    const priceClause: any = {};
    if (priceMin) priceClause.gte = parseFloat(priceMin);
    if (priceMax) priceClause.lte = parseFloat(priceMax);
    where.AND = where.AND || [];
    where.AND.push({
      OR: [
        { retailPrice: priceClause },
        {
          variants: {
            some: {
              isAvailable: true,
              inventoryQuantity: { gt: 0 },
              price: priceClause,
            },
          },
        },
      ],
    });
  }

  // ─── Variant-level filters (size) ─────────────────────────────────
  // The catalogue has TWO data shapes coexisting (verified 2026-04-16):
  //   A. Sapo / per-color-product pattern (production shape)
  //      Each colour is a separate Product. Variants are sizes only.
  //      option1 = "S" | "M" | "L" | "XL" | "Default", option2 = null.
  //
  //   B. Shopify canonical pattern (dev seed + future imports)
  //      One Product per style. Variants carry both axes.
  //      option1 = colour name, option2 = size letter.
  //
  // For SIZE filtering we accept either column. `OR` because Prisma
  // doesn't allow two `variants.some` clauses on the same query.
  // Variant must be priced AND in stock — the only meaningful "is this
  // sellable" check now that the top-level `retailPrice: { gt: 0 }` was
  // removed (see the comment in the `where` block above for why).
  const variantConstraints: any = {
    isAvailable: true,
    inventoryQuantity: { gt: 0 },
    price: { gt: 0 },
  };
  if (sizes.length > 0) {
    variantConstraints.OR = sizes.flatMap((s) => [
      { option1: { equals: s, mode: "insensitive" } },
      { option2: { equals: s, mode: "insensitive" } },
    ]);
  }
  where.variants = { some: variantConstraints };
  // Colour is applied in-memory below — the source of truth differs
  // by data shape (Product.title for shape A, variant.option1 for B).

  // Sort mapping. `featured` = default (createdAt desc, matches previous
  // "newest" behavior); `best-sellers` and `rating` omitted because the
  // schema doesn't have denormalized salesCount/averageRating columns.
  let orderBy: any;
  switch (sort) {
    case "price-asc":
    case "price-desc":
      // FIX 2026-06-07: price sort was ordering by the `retailPrice` COLUMN,
      // but ~38+ products are priced on VARIANTS (retailPrice = NULL) while
      // the grid shows the EFFECTIVE price (retailPrice ?? cheapest variant).
      // So NULL-priced products clumped at "nulls last" displaying a variant
      // price the sort never accounted for → the grid looked unordered
      // ("500k → 850k → 550k …"). Prisma can't ORDER BY
      // COALESCE(retailPrice, MIN(variant.price)), so we sort in JS after
      // fetch (see below). Safe because PAGE_SIZE=500 returns the entire
      // filtered catalog (~144 retail products) in one query, so the JS sort
      // covers the whole result set, not a slice. Stable DB order here; the
      // JS sort is authoritative for price.
      orderBy = [{ sortPosition: "asc" }, { createdAt: "desc" }];
      break;
    case "title-asc":
      orderBy = { title: "asc" };
      break;
    case "newest":
      orderBy = { createdAt: "desc" };
      break;
    case "featured":
    default:
      // Prefer pinned (sortPosition asc) then newest first
      orderBy = [{ sortPosition: "asc" }, { createdAt: "desc" }];
  }

  const [products, total] = await Promise.all([
    prisma.product.findMany({
      where,
      include: {
        variants: {
          where: { isAvailable: true },
          select: {
            id: true,
            option1: true,
            option2: true,
            price: true,
            compareAtPrice: true,
            inventoryQuantity: true,
          },
        },
        collection: { select: { name: true } },
        reviews: {
          where: { isPublished: true },
          select: { rating: true },
        },
      },
      orderBy,
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    }),
    prisma.product.count({ where }),
  ]);

  // Price sorts: order by EFFECTIVE price (retailPrice ?? cheapest available
  // variant) so the order matches the price shown on each card. Done in JS
  // because Prisma can't ORDER BY a COALESCE/MIN expression; PAGE_SIZE=500
  // means `products` already holds the entire filtered set.
  if (sort === "price-asc" || sort === "price-desc") {
    const effPrice = (p: (typeof products)[number]): number => {
      if (p.retailPrice != null && Number(p.retailPrice) > 0) {
        return Number(p.retailPrice);
      }
      const vp = (p.variants ?? [])
        .map((v) => Number(v.price))
        .filter((x) => x > 0);
      return vp.length ? Math.min(...vp) : Number.POSITIVE_INFINITY;
    };
    products.sort((a, b) =>
      sort === "price-asc" ? effPrice(a) - effPrice(b) : effPrice(b) - effPrice(a),
    );
  }

  // Get available filter options (distinct values only). Only include values
  // that have at least one sellable product (real price + in-stock variant),
  // otherwise the sidebar lists dead filters that return empty results.
  const filterableProductWhere = {
    shopId: shop.id,
    isRetailEnabled: true,
    status: "ACTIVE" as const,
    retailPrice: { gt: 0 },
    variants: {
      some: {
        isAvailable: true,
        inventoryQuantity: { gt: 0 },
      },
    },
  };

  // Price bounds for the dual-thumb slider
  const priceAgg = await prisma.product.aggregate({
    _min: { retailPrice: true },
    _max: { retailPrice: true },
    where: filterableProductWhere,
  });
  const priceFloor = Math.floor(Number(priceAgg._min.retailPrice || 0));
  const priceCeiling = Math.ceil(Number(priceAgg._max.retailPrice || 5000000));

  // T1.5: each facet must be counted against the currently-applied filters
  // EXCEPT its own dimension. Faceted search convention — clicking "Activity:
  // Tennis" should show size counts that reflect only Tennis products. We
  // build one where clause per dimension by copying the main filter state and
  // omitting the dimension being counted.
  type FacetDim = "activity" | "fabric" | "category" | "fit" | "color" | "size";
  const buildFacetWhere = (exclude: FacetDim): any => {
    const w: any = {
      shopId: shop.id,
      isRetailEnabled: true,
      status: "ACTIVE",
      retailPrice: { gt: 0 },
    };
    if (exclude !== "activity") {
      if (activities.length === 1) w.activity = activities[0];
      else if (activities.length > 1) w.activity = { in: activities };
    }
    if (exclude !== "fabric") {
      if (fabrics.length === 1) w.fabric = fabrics[0];
      else if (fabrics.length > 1) w.fabric = { in: fabrics };
    }
    if (exclude !== "category") {
      if (categories.length === 1) w.productType = categories[0];
      else if (categories.length > 1) w.productType = { in: categories };
    }
    if (exclude !== "fit") {
      if (fits.length === 1) w.fitGuideTop = fits[0];
      else if (fits.length > 1) w.fitGuideTop = { in: fits };
    }
    if (newIn) {
      // Mirror the main loader's date-based "new" logic so facet counts match.
      const newArrivalsCutoff = new Date();
      newArrivalsCutoff.setDate(newArrivalsCutoff.getDate() - shop.newArrivalDays);
      w.OR = [
        { tags: { contains: "new" } },
        { createdAt: { gte: newArrivalsCutoff } },
      ];
    }
    if (onSale) w.retailComparePrice = { not: null };
    if (priceMin || priceMax) {
      w.retailPrice = { gt: 0 };
      if (priceMin) w.retailPrice.gte = parseFloat(priceMin);
      if (priceMax) w.retailPrice.lte = parseFloat(priceMax);
    }
    // Size lives in variant.option1 in this dataset (not option2). Color
    // lives in the product TITLE, not in any variant column — so the
    // facet builder can't push the colour filter into Prisma. Caller
    // applies colour-on-title filtering in-memory before counting.
    const variantC: any = { isAvailable: true, inventoryQuantity: { gt: 0 } };
    if (exclude !== "size") {
      if (sizes.length === 1) variantC.option1 = sizes[0];
      else if (sizes.length > 1) variantC.option1 = { in: sizes };
    }
    w.variants = { some: variantC };
    return w;
  };

  // Counted facets (CRO 2026-06-25): groupBy replaces the old `distinct`
  // queries so every facet value carries its product count under the
  // currently-applied filters (own dimension excluded — standard faceted-
  // search semantics). FilterSidebar renders "(n)" when count is present.
  const [activityRows, fabricRows, categoryRows, fitRows, collectionRows] = await Promise.all([
    prisma.product.groupBy({
      by: ["activity"],
      where: { ...buildFacetWhere("activity"), activity: { not: null } },
      _count: { _all: true },
    }),
    prisma.product.groupBy({
      by: ["fabric"],
      where: { ...buildFacetWhere("fabric"), fabric: { not: null } },
      _count: { _all: true },
    }),
    prisma.product.groupBy({
      by: ["productType"],
      where: { ...buildFacetWhere("category"), productType: { not: null } },
      _count: { _all: true },
    }),
    prisma.product.groupBy({
      by: ["fitGuideTop"],
      where: { ...buildFacetWhere("fit"), fitGuideTop: { not: null } },
      _count: { _all: true },
    }),
    prisma.collection.findMany({
      where: { shopId: shop.id, isActive: true },
      select: { name: true },
      orderBy: { sortOrder: "asc" },
    }),
  ]);

  const allActivities = activityRows.map((p) => p.activity).filter(Boolean) as string[];
  const allFabrics = fabricRows.map((p) => p.fabric).filter(Boolean) as string[];
  const allCategories = categoryRows.map((p) => p.productType).filter(Boolean) as string[];
  const allFits = fitRows.map((p) => p.fitGuideTop).filter(Boolean) as string[];
  const collections = collectionRows.map((c) => c.name);

  // value → product count per facet dimension. Assembled into the
  // `facetCounts` loader field; sizes + colour families are added below.
  const facetCounts: Record<string, Record<string, number>> = {
    activity: Object.fromEntries(
      activityRows.filter((r) => r.activity).map((r) => [r.activity as string, r._count._all]),
    ),
    fabric: Object.fromEntries(
      fabricRows.filter((r) => r.fabric).map((r) => [r.fabric as string, r._count._all]),
    ),
    category: Object.fromEntries(
      categoryRows.filter((r) => r.productType).map((r) => [r.productType as string, r._count._all]),
    ),
    fit: Object.fromEntries(
      fitRows.filter((r) => r.fitGuideTop).map((r) => [r.fitGuideTop as string, r._count._all]),
    ),
    size: {},
    colorFamily: {},
  };

  // ─── Category strip tiles — static curated images (same as homepage) ─── //
  // Links use the DB activity values (court/studio/train/lounge) so the
  // "active" highlight check (`cat.link.includes("activity=${activity}")`)
  // matches after the loader has already run `aliasActivity()` on the URL
  // param. The ACTIVITY_URL_TO_DB alias above still accepts the legacy
  // tennis/yoga/gym URLs from SEO redirects + bookmarks — this just
  // standardises the internal strip on the canonical DB values.
  // (Fixes audit finding B5.)
  //
  // Accessories thumbnail: pulled dynamically from the first
  // in-stock accessory product's featured image rather than a static
  // /activity/*.jpg, because merchant flagged 2026-05-13 that the
  // ACC card was showing a workout-set image ("Why ACC show as a
  // set?"). Reusing /activity/train.jpg made sense when ACC didn't
  // exist as a category but is misleading now. Defaults to
  // /activity/lounge.jpg if no accessory product happens to be in
  // stock — better to show a generic editorial than a 404.
  //
  // Audit 2026-05-13: accessory products carry `productType: "Accessories"`
  // (capital A) and `tags: null` — NOT `tags: "accessories"`. Querying
  // by tag returned 0 rows; productType is the right axis.
  // Audit 2026-05-13 (round 2): naive "first by sortPosition" picked
  // inspire-visor-black (sp=28) whose featuredImageUrl points at the
  // R2 `product-backup` bucket — which 404s in production for 6 of 9
  // accessory products. PDPs render OK because `resolveFeaturedImage`
  // falls back to backupImages/mediaOverrides, but the activity-strip
  // thumbnail uses featuredImageUrl directly and a dead R2 URL renders
  // a broken-image icon. Bias the query toward known-good CDN hosts
  // (cdn.diolo.com primary, bizweb.dktcdn.net legacy fallback)
  // so the thumbnail is always loadable. If nothing matches, fall
  // through to the static editorial.
  // Text-tab category strip (2026-06-11 menswear rebrand): the old
  // image-tile strip carried Diolo-era womenswear photography under
  // /activity/*.jpg, so the tiles are now plain underlined tabs in the
  // 247 design language. Keys are the canonical DB activity values.
  const categoryStrip = [
    { label: "All", labelVi: "Tất cả", link: "/products", img: null },
    { label: "New In", labelVi: "Mới", link: "/products?tag=new", img: null },
    { label: "Train", labelVi: "Tập", link: "/products?activity=train", img: null },
    { label: "Court", labelVi: "Sân", link: "/products?activity=court", img: null },
    { label: "Run", labelVi: "Chạy", link: "/products?activity=run", img: null },
    { label: "Lounge", labelVi: "Nghỉ ngơi", link: "/products?activity=lounge", img: null },
  ];

  // ─── Colours for the sidebar facet (dual-shape, counted) ─────────
  // Colours can come from EITHER source:
  //   • Product.title → getProductColor()  (per-color-product shape)
  //   • Variant.option1                    (canonical Shopify shape)
  // We union both, then keep only colour names with a known swatch hex.
  // Counts use buildFacetWhere("color") — own dimension excluded — and are
  // tallied per FAMILY from the title-derived colour (the shape this
  // catalog uses); variant-only colours still LIST but may not count.
  const [colorTitleRows, colorOpt1Rows] = await Promise.all([
    prisma.product.findMany({
      where: buildFacetWhere("color"),
      select: { title: true },
    }),
    prisma.productVariant.findMany({
      where: {
        product: buildFacetWhere("color"),
        isAvailable: true,
        inventoryQuantity: { gt: 0 },
        option1: { not: null },
      },
      select: { option1: true },
      distinct: ["option1"],
    }),
  ]);
  const colorsFromTitles = colorTitleRows
    .map((p) => getProductColor(p.title))
    .filter((c) => !!c);
  const colorsFromVariants = colorOpt1Rows
    .map((r) => r.option1!)
    // Drop size letters + "Default" — those are sizes, not colours.
    .filter((v) => !/^(s|m|l|xl|xxl|xs|default(\s+title)?)$/i.test(v));
  const distinctColorsAll = [...new Set([...colorsFromTitles, ...colorsFromVariants])];
  const distinctColors = distinctColorsAll.filter((c) => c in COLOR_HEX);
  for (const c of colorsFromTitles) {
    const fam = colorFamilyOf(c as string);
    if (fam) facetCounts.colorFamily[fam] = (facetCounts.colorFamily[fam] ?? 0) + 1;
  }

  // ─── Sizes for the sidebar facet (dual-shape, counted) ───────────
  // Sizes can be in option1 (per-color-product shape) OR option2
  // (canonical shape). One row fetch replaces the two old `distinct`
  // queries so we can count DISTINCT PRODUCTS per size in memory.
  const sizeVariantRows = await prisma.productVariant.findMany({
    where: {
      product: buildFacetWhere("size"),
      isAvailable: true,
      inventoryQuantity: { gt: 0 },
    },
    select: { productId: true, option1: true, option2: true },
  });
  const isSizeToken = (s: string | null): s is string =>
    !!s &&
    !s.toLowerCase().startsWith("default") &&
    // Only keep things that LOOK like sizes (letters or numbers, short).
    // Drops colour names that ended up in option1 in shape B.
    /^[a-z0-9/-]{1,8}$/i.test(s);
  const sizeProductSets = new Map<string, Set<string>>();
  for (const row of sizeVariantRows) {
    for (const cand of [row.option1, row.option2]) {
      if (!isSizeToken(cand)) continue;
      let set = sizeProductSets.get(cand);
      if (!set) {
        set = new Set();
        sizeProductSets.set(cand, set);
      }
      set.add(row.productId);
    }
  }
  const distinctSizes = [...sizeProductSets.keys()];
  for (const [size, prodSet] of sizeProductSets) {
    facetCounts.size[size] = prodSet.size;
  }

  // The Prisma `where.variants.some` clause already handles color + size
  // filtering, but we still apply it in-memory for the legacy single-value
  // path (some callers hit this page with `?color=Black` without using the
  // sidebar). Also enforce the `onSale` column-comparison that Prisma can't
  // express natively: compare_price > retail_price.
  let filteredProducts = products;
  if (onSale) {
    filteredProducts = filteredProducts.filter((p) => {
      const comp = p.retailComparePrice ? Number(p.retailComparePrice) : 0;
      const price = p.retailPrice ? Number(p.retailPrice) : 0;
      return comp > price;
    });
  }

  // ─── ?type=<group> filter (Shop > By Type mega menu) ─────────────
  // Title-keyword whitelist match — see TYPE_GROUP_KEYWORDS at the top
  // of this file for the keyword sets per group. Both the curated EN
  // title and the raw VN title are tested so the filter works in both
  // locales (e.g. "Áo bra" in VN still matches the bra keyword).
  if (typeGroup && TYPE_GROUP_KEYWORDS[typeGroup]) {
    const re = TYPE_GROUP_KEYWORDS[typeGroup];
    filteredProducts = filteredProducts.filter((p) => {
      const titleStr = `${p.title || ""} ${(p as any).titleEn || ""}`;
      return re.test(titleStr);
    });
  }
  // ─── Color filter (in-memory, dual-shape) ────────────────────────
  // Match a product if EITHER:
  //   • Product.title contains a color suffix matching one of the
  //     requested colors (per-color-product shape, e.g.
  //     "Celebré Capri Leggings - Black (Đen)" → "Black"), OR
  //   • Any variant's option1 matches (canonical Shopify shape).
  // Lowercased for case-insensitive comparison.
  const colorsLower = colors.map((c) => c.toLowerCase());
  if (colors.length > 0) {
    filteredProducts = filteredProducts.filter((p) => {
      const titleColor = getProductColor(p.title).toLowerCase();
      if (titleColor && colorsLower.includes(titleColor)) return true;
      return p.variants.some((v) =>
        colorsLower.includes((v.option1 || "").toLowerCase()),
      );
    });
  }

  // ─── Size filter (in-memory, dual-shape) ─────────────────────────
  // Sizes live in option1 (per-color-product shape) OR option2 (canonical
  // shape). Match either column. Drops "Default"/"Default Title" pseudo-
  // sizes for products with no real size axis.
  const sizesLower = sizes.map((s) => s.toLowerCase());
  if (sizes.length > 0) {
    filteredProducts = filteredProducts.filter((p) =>
      p.variants.some((v) => {
        if (v.inventoryQuantity <= 0) return false;
        const o1 = (v.option1 || "").toLowerCase();
        const o2 = (v.option2 || "").toLowerCase();
        return sizesLower.includes(o1) || sizesLower.includes(o2);
      }),
    );
  }

  // T3.36: single pass that computes review stats and strips the raw
  // reviews array from the payload. Keeping this inline (rather than
  // factoring into a helper) because the TypeScript inference for
  // `useLoaderData<typeof loader>()` breaks when the helper is used.
  const productsWithReviews = filteredProducts.map((p) => {
    const reviewCount = p.reviews?.length || 0;
    const reviewAvg = reviewCount > 0
      ? Math.round((p.reviews.reduce((sum: number, r: { rating: number }) => sum + r.rating, 0) / reviewCount) * 10) / 10
      : 0;
    const { reviews, ...rest } = p;
    return { ...rest, reviewAvg, reviewCount };
  });

  // ─── Sibling products: same base name, different color ─── //
  // Use the shared helper that splits on " - " (bilingual titles like
  // "Ace - Black (Đen)" → "Ace"). The old local version tried to strip
  // trailing color words, which failed on parenthetical Vietnamese titles.
  function getBaseName(title: string): string {
    return getProductBaseName(title);
  }

  // Group products by base name for sibling resolution
  const baseNameMap = new Map<string, typeof productsWithReviews>();
  for (const p of productsWithReviews) {
    const base = getBaseName(p.title);
    if (!baseNameMap.has(base)) baseNameMap.set(base, []);
    baseNameMap.get(base)!.push(p);
  }

  // For products not on this page but sharing a base name, fetch siblings
  const baseNames = [...new Set(productsWithReviews.map((p) => getBaseName(p.title)))];
  const siblingCandidates = baseNames.length > 0
    ? await prisma.product.findMany({
        where: {
          shopId: shop.id,
          isRetailEnabled: true,
          status: "ACTIVE",
          retailPrice: { gt: 0 },
          id: { notIn: productsWithReviews.map((p) => p.id) },
        },
        select: {
          id: true,
          handle: true,
          title: true,
          titleEn: true,
          featuredImageUrl: true,
          retailPrice: true,
          retailComparePrice: true,
          preOrderEnabled: true,
          variants: {
            where: { isAvailable: true },
            select: { inventoryQuantity: true, option1: true },
            take: 5,
          },
        },
      })
    : [];

  // Build sibling map keyed by base name
  const siblingByBase = new Map<string, any[]>();
  for (const s of siblingCandidates) {
    const base = getBaseName(s.title);
    if (baseNames.includes(base)) {
      if (!siblingByBase.has(base)) siblingByBase.set(base, []);
      const color = getProductColor(s.title) || null;
      siblingByBase.get(base)!.push({
        id: s.id,
        handle: s.handle,
        title: s.title,
        titleEn: s.titleEn || null,
        featuredImageUrl: s.featuredImageUrl,
        retailPrice: s.retailPrice ? Number(s.retailPrice) : 0,
        retailComparePrice: s.retailComparePrice ? Number(s.retailComparePrice) : null,
        color,
        preOrderEnabled: s.preOrderEnabled,
        totalInventory: s.variants.reduce((sum: number, v: any) => sum + v.inventoryQuantity, 0),
      });
    }
  }

  // Attach siblings to each product
  const productsWithSiblings = productsWithReviews.map((p) => {
    const base = getBaseName(p.title);
    const selfColor = getProductColor(p.title) || null;
    const selfSibling = {
      id: p.id,
      handle: p.handle,
      title: p.title,
      titleEn: (p as any).titleEn || null,
      featuredImageUrl: p.featuredImageUrl,
      retailPrice: p.retailPrice ? Number(p.retailPrice) : 0,
      retailComparePrice: p.retailComparePrice ? Number(p.retailComparePrice) : null,
      color: selfColor,
      preOrderEnabled: (p as any).preOrderEnabled || false,
      totalInventory: p.variants.reduce((sum: number, v: any) => sum + v.inventoryQuantity, 0),
    };

    // Same-page siblings
    const pageGroup = (baseNameMap.get(base) || [])
      .filter((s) => s.id !== p.id)
      .map((s) => ({
        id: s.id,
        handle: s.handle,
        title: s.title,
        titleEn: (s as any).titleEn || null,
        featuredImageUrl: s.featuredImageUrl,
        retailPrice: s.retailPrice ? Number(s.retailPrice) : 0,
        retailComparePrice: s.retailComparePrice ? Number(s.retailComparePrice) : null,
        color: getProductColor(s.title) || null,
        preOrderEnabled: (s as any).preOrderEnabled || false,
        totalInventory: s.variants.reduce((sum: number, v: any) => sum + v.inventoryQuantity, 0),
      }));

    const offPageSiblings = siblingByBase.get(base) || [];
    const allSiblings = [selfSibling, ...pageGroup, ...offPageSiblings];

    // Deduplicate by id
    const seen = new Set<string>();
    const uniqueSiblings = allSiblings.filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });

    return { ...p, siblings: uniqueSiblings };
  });

  // ─── Apply market pricing if visitor is in a non-base-currency market ─── //
  const { market } = await resolveMarket(request, shop.id);
  const shopCurrency = shop.currency || "VND";

  if (market && market.currency !== shopCurrency) {
    const allVariants = productsWithSiblings.flatMap((p: any) =>
      p.variants.map((v: any) => ({
        id: v.id,
        price: Number(v.price),
        compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
      }))
    );

    if (allVariants.length > 0) {
      const marketPriceMap = await getMarketPrices(
        allVariants,
        market.marketId,
        shopCurrency,
        shop.id
      );

      for (const p of productsWithSiblings as any[]) {
        for (const v of p.variants) {
          const mp = marketPriceMap.get(v.id);
          if (mp) {
            v.price = mp.price;
            v.compareAtPrice = mp.compareAtPrice;
          }
        }
      }
    }
  }

  const hasInMemoryFilter = onSale || sizes.length > 0 || colors.length > 0 || (typeGroup != null && TYPE_GROUP_KEYWORDS[typeGroup] != null);
  // T3.2: expose absolute origin so the meta function can emit canonical URL
  const origin = new URL(request.url).origin.replace(/^http:\/\//, "https://");
  const displayCount = productsWithSiblings.length;

  // Diolo display rename — applied AFTER all sibling grouping so the
  // raw Sapo titles are still used for base-name keying. See
  // utils/productTitle.ts for the rule list.
  for (const p of productsWithSiblings as any[]) {
    p.title = renameProductTitle(p.title);
    if (p.titleEn) p.titleEn = renameProductTitle(p.titleEn);
    if (Array.isArray(p.siblings)) {
      for (const sib of p.siblings) {
        if (sib.title) sib.title = renameProductTitle(sib.title);
        if (sib.titleEn) sib.titleEn = renameProductTitle(sib.titleEn);
      }
    }
  }

  return json({
    products: productsWithSiblings,
    total: hasInMemoryFilter ? filteredProducts.length : total,
    displayCount,
    page,
    hasMore: page * PAGE_SIZE < (hasInMemoryFilter ? filteredProducts.length : total),
    filters: {
      activities: allActivities as string[],
      fabrics: allFabrics as string[],
      collections: collections as string[],
      // Aritzia-style: expose color FAMILIES (Beige, Black, Blue, …)
      // instead of every individual catalog color name. Filtered to
      // only the families that have at least one in-stock product so
      // we don't show "Yellow" if zero yellow products exist.
      colorFamilies: familiesPresentIn(distinctColors as string[]).map((f) => ({
        name: f.name,
        swatches: f.swatches,
      })),
      sizes: distinctSizes as string[],
      categories: allCategories as string[],
      fits: allFits as string[],
    },
    // value → product count per facet dimension (CRO 2026-06-25); the
    // client attaches these to FilterSidebar options as "(n)" labels.
    facetCounts,
    categoryStrip,
    priceFloor,
    priceCeiling,
    locale,
    origin,
  });
};

// ─── Skeleton placeholder for loading state ─── //
function ProductSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[3/4] bg-luxury-cream rounded" />
      <div className="mt-2 px-1.5 space-y-2">
        <div className="h-3 bg-luxury-cream rounded w-3/4" />
        <div className="h-3 bg-luxury-cream rounded w-1/3" />
      </div>
    </div>
  );
}

export default function ProductListing() {
  // Defensive guard — Sentry (REMIX-10, REMIX-Z) caught
  // "Cannot destructure property 'products' from null or undefined value"
  // on /products. Same pattern as PDP: useLoaderData briefly null during
  // a transition/error-boundary edge case. Returning null lets the next
  // render (or ErrorBoundary) take over without crashing.
  const loaderData = useLoaderData<typeof loader>();
  if (!loaderData) return null;
  const { products, total, displayCount, hasMore, filters, categoryStrip, priceFloor, priceCeiling, locale: serverLocale } = loaderData;
  const facetCounts = ((loaderData as any).facetCounts ?? {}) as Record<string, Record<string, number>>;
  // Grid view density (desktop): comfortable 4-up vs dense 5-up, persisted
  // per browser. SSR renders "comfort"; the stored preference applies after
  // hydration (a class swap, not content, so no mismatch risk).
  const [gridDensity, setGridDensityState] = useState<"comfort" | "dense">("comfort");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("diolo_plp_density");
      if (stored === "dense") setGridDensityState("dense");
    } catch { /* storage unavailable */ }
  }, []);
  const setGridDensity = (d: "comfort" | "dense") => {
    setGridDensityState(d);
    try { localStorage.setItem("diolo_plp_density", d); } catch { /* ignore */ }
  };
  const gridClass =
    gridDensity === "dense"
      ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 gap-y-10"
      : "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-y-12";
  const [searchParams, setSearchParams] = useSearchParams();
  const outletCtx = useOutletContext<{ locale?: Locale; currency?: string }>();
  const locale: Locale = outletCtx?.locale || (serverLocale as Locale) || "vi";
  const navigation = useNavigation();
  const isLoadingMore = navigation.state === "loading";

  const [quickViewProduct, setQuickViewProduct] = useState<any | null>(null);
  const [quickViewSize, setQuickViewSize] = useState<string | null>(null);

  // Local state for price range inputs
  const [localPriceMin, setLocalPriceMin] = useState(searchParams.get("priceMin") || "");
  const [localPriceMax, setLocalPriceMax] = useState(searchParams.get("priceMax") || "");

  // Mobile bottom sheet + its draft selection state
  const [sheetOpen, setSheetOpen] = useState(false);

  // M-10 FIX: Close mobile filter sheet when URL changes (e.g. clicking a
  // category strip link while the sheet is open).
  const searchParamsString = searchParams.toString();
  useEffect(() => {
    setSheetOpen(false);
  }, [searchParamsString]);

  // Infinite scroll ref
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Multi-value selections read from URL params
  const selectedMulti: Record<string, string[]> = useMemo(
    () => ({
      activity: searchParams.getAll("activity"),
      fabric: searchParams.getAll("fabric"),
      category: searchParams.getAll("category"),
      fit: searchParams.getAll("fit"),
      color: searchParams.getAll("color"),
      size: searchParams.getAll("size"),
    }),
    [searchParams],
  );

  const tag = searchParams.get("tag");
  // Default sort matches loader default — see comment in loader for why.
  // 2026-05-12: kept in lockstep with loader. Was "newest", flipped to
  // "featured" alongside the loader so the dropdown's selected state
  // matches what the server actually rendered (the merchant-curated
  // sortPosition order).
  const sort = searchParams.get("sort") || "featured";
  const search = searchParams.get("q");
  const collectionFilter = searchParams.get("collection");
  const newInActive = searchParams.get("newIn") === "1" || tag === "new";
  const onSaleActive = searchParams.get("onSale") === "1";

  // Legacy single-value accessors kept for title logic
  const activity = selectedMulti.activity[0] || null;
  const fabric = selectedMulti.fabric[0] || null;

  // Draft state for mobile sheet — seeded from current URL params whenever the
  // sheet opens so reopening after a previous apply shows the current state.
  const [draftSelected, setDraftSelected] = useState<Record<string, string[]>>(selectedMulti);
  const [draftNewIn, setDraftNewIn] = useState(newInActive);
  const [draftOnSale, setDraftOnSale] = useState(onSaleActive);
  const [draftPriceMin, setDraftPriceMin] = useState(localPriceMin);
  const [draftPriceMax, setDraftPriceMax] = useState(localPriceMax);

  useEffect(() => {
    if (sheetOpen) {
      setDraftSelected(selectedMulti);
      setDraftNewIn(newInActive);
      setDraftOnSale(onSaleActive);
      setDraftPriceMin(searchParams.get("priceMin") || "");
      setDraftPriceMax(searchParams.get("priceMax") || "");
    }
    // intentional: refresh draft on open only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen]);

  // Count of active filters for display
  const activeCount = useMemo(() => {
    let n = 0;
    for (const key of Object.keys(selectedMulti)) n += selectedMulti[key].length;
    if (newInActive) n++;
    if (onSaleActive) n++;
    if (searchParams.get("priceMin")) n++;
    if (searchParams.get("priceMax")) n++;
    if (collectionFilter) n++;
    return n;
  }, [selectedMulti, newInActive, onSaleActive, searchParams, collectionFilter]);

  // URL commit helpers
  const commitSearchParams = useCallback(
    (next: URLSearchParams) => {
      next.delete("page");
      setSearchParams(next);
    },
    [setSearchParams],
  );

  const toggleFilterValue = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      const existing = next.getAll(key);
      next.delete(key);
      if (existing.includes(value)) {
        // Remove
        existing.filter((v) => v !== value).forEach((v) => next.append(key, v));
      } else {
        // Add
        existing.forEach((v) => next.append(key, v));
        next.append(key, value);
      }
      commitSearchParams(next);
    },
    [searchParams, commitSearchParams],
  );

  const clearFilterGroup = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      next.delete(key);
      commitSearchParams(next);
    },
    [searchParams, commitSearchParams],
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams({});
    setLocalPriceMin("");
    setLocalPriceMax("");
  }, [setSearchParams]);

  const toggleNewIn = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (newInActive) {
      next.delete("newIn");
      if (next.get("tag") === "new") next.delete("tag");
    } else {
      next.set("newIn", "1");
    }
    commitSearchParams(next);
  }, [searchParams, newInActive, commitSearchParams]);

  const toggleOnSale = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (onSaleActive) next.delete("onSale");
    else next.set("onSale", "1");
    commitSearchParams(next);
  }, [searchParams, onSaleActive, commitSearchParams]);

  const applyPriceRange = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (localPriceMin) next.set("priceMin", localPriceMin);
    else next.delete("priceMin");
    if (localPriceMax) next.set("priceMax", localPriceMax);
    else next.delete("priceMax");
    commitSearchParams(next);
  }, [searchParams, localPriceMin, localPriceMax, commitSearchParams]);

  const setSort = (value: string) => {
    const next = new URLSearchParams(searchParams);
    // Newest is the default — clear the param to keep URLs clean.
    if (value === "newest") next.delete("sort");
    else next.set("sort", value);
    commitSearchParams(next);
  };

  const loadMore = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    const currentPage = parseInt(next.get("page") || "1", 10);
    next.set("page", String(currentPage + 1));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Mobile sheet draft handlers
  const draftToggle = useCallback((key: string, value: string) => {
    setDraftSelected((prev) => {
      const existing = prev[key] || [];
      return {
        ...prev,
        [key]: existing.includes(value)
          ? existing.filter((v) => v !== value)
          : [...existing, value],
      };
    });
  }, []);

  const draftClearGroup = useCallback((key: string) => {
    setDraftSelected((prev) => ({ ...prev, [key]: [] }));
  }, []);

  const draftPriceChange = useCallback((min: string, max: string) => {
    setDraftPriceMin(min);
    setDraftPriceMax(max);
  }, []);

  const applySheet = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    // Reset all multi-value groups first, then repopulate from draft
    for (const key of Object.keys(draftSelected)) {
      next.delete(key);
      for (const v of draftSelected[key]) next.append(key, v);
    }
    if (draftNewIn) next.set("newIn", "1");
    else {
      next.delete("newIn");
      if (next.get("tag") === "new") next.delete("tag");
    }
    if (draftOnSale) next.set("onSale", "1");
    else next.delete("onSale");
    if (draftPriceMin) next.set("priceMin", draftPriceMin);
    else next.delete("priceMin");
    if (draftPriceMax) next.set("priceMax", draftPriceMax);
    else next.delete("priceMax");
    setLocalPriceMin(draftPriceMin);
    setLocalPriceMax(draftPriceMax);
    commitSearchParams(next);
    setSheetOpen(false);
  }, [searchParams, draftSelected, draftNewIn, draftOnSale, draftPriceMin, draftPriceMax, commitSearchParams]);

  const clearSheet = useCallback(() => {
    setDraftSelected({
      activity: [],
      fabric: [],
      category: [],
      fit: [],
      color: [],
      size: [],
    });
    setDraftNewIn(false);
    setDraftOnSale(false);
    setDraftPriceMin("");
    setDraftPriceMax("");
  }, []);

  // Quick view modal -- close on Escape
  useEffect(() => {
    if (!quickViewProduct) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQuickViewProduct(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [quickViewProduct]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || isLoadingMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "400px" }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  // Title — map DB activity values to display names. Accepts both URL
  // aliases (tennis/yoga/gym) and DB values (court/studio/train) so
  // existing links + legacy bookmarks keep rendering the right title.
  const activityDisplayName: Record<string, string> = {
    court: "Court",
    studio: "Studio",
    train: "Train",
    lounge: "Lounge",
    run: "Run",
    // URL-alias passthroughs — kept for safety if something slips past
    // the aliasActivity() normaliser above.
    tennis: "Court",
    yoga: "Studio",
    gym: "Train",
  };
  let title = t("allProducts", locale);
  if (activity) title = activityDisplayName[activity] || activity.charAt(0).toUpperCase() + activity.slice(1);
  if (fabric) title = fabric.charAt(0).toUpperCase() + fabric.slice(1) + (locale === "en" ? " Collection" : "");
  if (newInActive) title = t("newArrivalsTitle", locale);
  if (search) title = `${t("resultsFor", locale)} "${search}"`;
  if (collectionFilter) title = collectionFilter;

  // Sort options. `featured` is the default and uses merchant-curated
  // sortPosition (asc) with createdAt (desc) as tiebreaker. Restored
  // to the dropdown 2026-05-12 alongside the loader default flip back
  // to "featured" — see the loader comment for the full story.
  const sortOptions = [
    { value: "featured", label: t("sortFeatured", locale) },
    { value: "newest", label: t("sortNewest", locale) },
    { value: "price-asc", label: t("priceLowHigh", locale) },
    { value: "price-desc", label: t("priceHighLow", locale) },
    { value: "title-asc", label: t("titleAZ", locale) },
  ];

  // Build filter groups consumed by both Sidebar and BottomSheet.
  // Each option carries its live result count (CRO 2026-06-25) — the
  // sidebar renders "(n)" when present.
  const filterGroups: FilterGroup[] = useMemo(() => {
    const groups: FilterGroup[] = [];
    const f = filters as any;
    const countOf = (dim: string, value: string): number | undefined =>
      facetCounts?.[dim]?.[value];
    if (f.categories?.length) {
      groups.push({
        key: "category",
        label: t("category", locale),
        options: (f.categories as string[]).map((v) => ({ value: v, count: countOf("category", v) })),
        kind: "checkbox",
        defaultOpen: true,
      });
    }
    if (f.activities.length) {
      groups.push({
        key: "activity",
        label: t("activity", locale),
        options: (f.activities as string[]).map((v) => ({ value: v, count: countOf("activity", v) })),
        kind: "checkbox",
        defaultOpen: true,
      });
    }
    if (f.sizes.length) {
      groups.push({
        key: "size",
        label: t("size", locale),
        options: (f.sizes as string[]).map((v) => ({ value: v, count: countOf("size", v) })),
        kind: "pill",
        defaultOpen: true,
      });
    }
    if ((f as any).colorFamilies?.length) {
      groups.push({
        key: "color",
        label: t("color", locale),
        // Family options: each row is a checkbox + 2-swatch preview + name.
        // The `swatches` tuple is what the new "swatch-family" renderer uses;
        // `value` is the family name and goes into the URL on selection.
        options: ((f as any).colorFamilies as Array<{ name: string; swatches: [string, string] }>)
          .map((fam) => ({ value: fam.name, label: fam.name, swatches: fam.swatches, count: countOf("colorFamily", fam.name) })),
        kind: "swatch-family",
        defaultOpen: true,
      });
    }
    if (f.fabrics.length) {
      groups.push({
        key: "fabric",
        label: t("fabric", locale),
        options: (f.fabrics as string[]).map((v) => ({ value: v, count: countOf("fabric", v) })),
        kind: "checkbox",
        defaultOpen: false,
      });
    }
    if ((filters as any).fits?.length) {
      groups.push({
        key: "fit",
        label: t("fit", locale),
        options: (filters as any).fits.map((v: string) => ({
          value: v,
          count: countOf("fit", v),
          label:
            v === "TRUE_TO_SIZE"
              ? t("trueToSize", locale)
              : v === "RUNS_SMALL"
              ? t("runsSmall", locale)
              : v === "RUNS_LARGE"
              ? t("runsLarge", locale)
              : v,
        })),
        kind: "checkbox",
        defaultOpen: false,
      });
    }
    return groups;
  }, [filters, facetCounts, locale]);

  return (
    <div className="mx-auto max-w-[1440px] px-0 sm:px-6 lg:px-8 py-6">
      {/* ─── Page Header ─── */}
      {/* h1 keeps the existing small/quiet styling but is now semantic so
          Google has a clear page heading on the listing route. SEO audits
          (and a11y tools) flagged the missing h1 here. */}
      <div className="px-4 sm:px-0 mb-2">
        <h1 className="df-headline text-[26px] sm:text-[32px]">
          {title}
        </h1>
      </div>

      {/* ─── Sub-Category Strip (underlined text tabs) ─── */}
      <div className="px-4 sm:px-0 mt-5 mb-6 border-b border-[#e5e5e5]">
        <div className="overflow-x-auto flex gap-6 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          {categoryStrip.map((cat: { label: string; labelVi: string; link: string; img: string | null }) => {
            const isActive =
              (cat.label === "All" && !activity && !tag && !collectionFilter) ||
              (cat.link.includes("tag=new") && tag === "new") ||
              (cat.link.includes(`activity=${activity}`) && activity) ||
              (cat.link.includes("tag=accessories") && tag === "accessories") ||
              (cat.link.includes(`collection=${collectionFilter}`) && collectionFilter);
            const displayLabel = locale === "vi" ? cat.labelVi : cat.label;
            return (
              <Link
                key={cat.label}
                to={cat.link}
                className={`whitespace-nowrap shrink-0 pb-3 -mb-px text-[11px] uppercase tracking-[0.12em] border-b transition-colors ${
                  isActive
                    ? "border-black text-black"
                    : "border-transparent text-[#737373] hover:text-black"
                }`}
              >
                {displayLabel}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ─── Product count + view density + sort bar ─── */}
      <div className="flex items-center justify-between border-b border-[#e0e0e0] px-4 sm:px-0 py-3 mb-4">
        <p className="text-[11px] uppercase tracking-[0.08em] text-[#767676]">
          {t(displayCount === 1 ? "showingResultOne" : "showingResults", locale).replace("{count}", String(displayCount))}
        </p>
        <div className="flex items-center gap-5">
          {/* Grid density toggle (desktop only) — comfortable 4-up vs dense
              5-up. Persisted per browser. (CRO 2026-06-25) */}
          <div className="hidden lg:flex items-center gap-1.5" role="group" aria-label={locale === "vi" ? "Chế độ xem" : "View density"}>
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#737373] mr-0.5">
              {locale === "vi" ? "Xem" : "View"}
            </span>
            {([
              { key: "comfort" as const, cols: 3 },
              { key: "dense" as const, cols: 4 },
            ]).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setGridDensity(opt.key)}
                aria-pressed={gridDensity === opt.key}
                aria-label={`${opt.cols + 1} columns`}
                className={`flex items-end gap-[2px] p-1 transition-opacity ${gridDensity === opt.key ? "opacity-100" : "opacity-35 hover:opacity-70"}`}
              >
                {Array.from({ length: opt.cols }).map((_, i) => (
                  <span key={i} className="block h-3 w-[5px] bg-black" />
                ))}
              </button>
            ))}
          </div>
          <SortDropdown
            value={sort}
            options={sortOptions}
            onChange={setSort}
            locale={locale}
          />
        </div>
      </div>

      {/* ─── 2-column layout: sidebar (desktop only) + main grid ─── */}
      <div className="flex gap-8 px-4 sm:px-0">
        <FilterSidebar
          groups={filterGroups}
          selected={selectedMulti}
          onToggle={toggleFilterValue}
          onClearAll={clearAllFilters}
          onClearGroup={clearFilterGroup}
          priceMin={localPriceMin}
          priceMax={localPriceMax}
          onPriceChange={(min, max) => {
            setLocalPriceMin(min);
            setLocalPriceMax(max);
          }}
          onPriceApply={applyPriceRange}
          activeCount={activeCount}
          locale={locale}
          newInActive={newInActive}
          onNewInToggle={toggleNewIn}
          onSaleActive={onSaleActive}
          onOnSaleToggle={toggleOnSale}
          priceFloor={priceFloor}
          priceCeiling={priceCeiling}
        />

        {/* ─── Product Grid ─── */}
        <div className="flex-1 min-w-0">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-sm mb-4">{t("noProductsFound", locale)}</p>
              <div className="flex items-center justify-center gap-3">
                {activeCount > 0 && (
                  <button
                    onClick={() => setSearchParams(new URLSearchParams())}
                    className="sf-btn-secondary sf-btn-small"
                  >
                    {locale === "vi" ? "Xoá bộ lọc" : "Clear All Filters"}
                  </button>
                )}
                <Link to="/products" className="sf-btn-secondary sf-btn-small">
                  {t("viewAllProducts", locale)}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className={`${gridClass} sf-stagger`}>
                {(products as any[]).filter(Boolean).map((product: any, index: number) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    enablePeek={index === 0}
                    siblings={product.siblings}
                    locale={locale}
                    onQuickView={() => {
                      setQuickViewProduct(product);
                      setQuickViewSize(null);
                    }}
                  />
                ))}
              </div>

              {/* Skeleton loading when fetching next page */}
              {isLoadingMore && (
                <div className={`${gridClass} mt-4`}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <ProductSkeleton key={`skel-${i}`} />
                  ))}
                </div>
              )}

              {/* Infinite scroll sentinel */}
              <div ref={loadMoreRef} className="h-1" />

              {/* End of results */}
              {!hasMore && (products as any[]).length > 0 && !isLoadingMore && (
                <p className="text-center text-[12px] text-[#999] uppercase tracking-[0.08em] py-8">
                  {locale === "vi" ? "Đã hiển thị tất cả sản phẩm" : "You've viewed all products"}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Mobile bottom sheet + trigger button ─── */}
      {/* T2.15: only mount (and therefore download) the bottom sheet chunk
          once the user actually opens it, not on first paint. */}
      {sheetOpen && (
        <Suspense fallback={null}>
          <FilterBottomSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            groups={filterGroups}
            draftSelected={draftSelected}
            onDraftToggle={draftToggle}
            onDraftClearGroup={draftClearGroup}
            onClearAll={clearSheet}
            onApply={applySheet}
            activeCount={activeCount}
            locale={locale}
            priceMin={draftPriceMin}
            priceMax={draftPriceMax}
            onPriceChange={draftPriceChange}
            newInActive={draftNewIn}
            onNewInToggle={() => setDraftNewIn((v) => !v)}
            onSaleActive={draftOnSale}
            onOnSaleToggle={() => setDraftOnSale((v) => !v)}
            resultCount={displayCount}
            priceFloor={priceFloor}
            priceCeiling={priceCeiling}
          />
        </Suspense>
      )}

      {/* Mobile floating "Filters" button */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="lg:hidden fixed bottom-6 right-4 z-40 h-9 px-4 bg-black text-white text-[11px] uppercase tracking-[0.1em] shadow-lg flex items-center gap-2"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M10 16h4" />
        </svg>
        {t("filters", locale)}
        {activeCount > 0 && (
          <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[9px] bg-white text-black rounded-full">
            {activeCount}
          </span>
        )}
      </button>

      {/* ─── Quick View Modal ─── */}
      {quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          locale={locale}
          selectedSize={quickViewSize}
          onSelectSize={setQuickViewSize}
          onClose={() => setQuickViewProduct(null)}
        />
      )}
    </div>
  );
}

// ─── Quick View Modal Component ─── //
function QuickViewModal({
  product,
  locale,
  selectedSize,
  onSelectSize,
  onClose,
}: {
  product: any;
  locale: Locale;
  selectedSize: string | null;
  onSelectSize: (s: string | null) => void;
  onClose: () => void;
}) {
  const { addToCart, currency } = useOutletContext<{ addToCart: (item: any) => void; currency?: string }>();
  const ccy = currency || "VND";
  const [added, setAdded] = useState(false);

  const rawOpt2 = [...new Set(product.variants.map((v: any) => v.option2).filter(Boolean))] as string[];
  const rawOpt1 = [...new Set(product.variants.map((v: any) => v.option1).filter(Boolean))] as string[];
  const opt1IsSize = rawOpt2.length === 0 && rawOpt1.length > 0;
  const sizes = opt1IsSize ? rawOpt1 : rawOpt2;

  // Effective price = retailPrice ?? cheapest available variant — matches the
  // loader's price-sort key so sorted order lines up with the prices shown.
  const _variantPrices = (product.variants ?? [])
    .map((v: any) => Number(v.price))
    .filter((x: number) => x > 0);
  const price = product.retailPrice
    ? Number(product.retailPrice)
    : _variantPrices.length
      ? Math.min(..._variantPrices)
      : 0;
  const displayTitle = titleForLocale(product.title, locale, product.titleEn);

  const isSizeInStock = (size: string) => {
    if (opt1IsSize) return product.variants.some((v: any) => v.option1 === size && v.inventoryQuantity > 0);
    return product.variants.some((v: any) => v.option2 === size && v.inventoryQuantity > 0);
  };

  const handleAddToBag = () => {
    if (!selectedSize) return;
    const variant = opt1IsSize
      ? product.variants.find((v: any) => v.option1 === selectedSize && v.inventoryQuantity > 0)
      : product.variants.find((v: any) => v.option2 === selectedSize && v.inventoryQuantity > 0);
    if (!variant) return;

    addToCart({
      variantId: variant.id,
      productHandle: product.handle || product.id,
      productTitle: product.title,
      variantTitle: opt1IsSize ? selectedSize : `${variant.option1 || ""} / ${selectedSize}`.trim(),
      color: opt1IsSize ? "" : (variant.option1 || ""),
      size: selectedSize,
      price: Number(variant.price),
      comparePrice: variant.compareAtPrice ? Number(variant.compareAtPrice) : undefined,
      imageUrl: product.featuredImageUrl || undefined,
      quantity: 1,
      maxQuantity: variant.inventoryQuantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Modal */}
      <div className="relative bg-white z-[101] max-w-lg w-full mx-4 rounded-lg shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full hover:bg-white transition-colors"
          aria-label={t("close", locale)}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col sm:flex-row">
          {/* Image */}
          <div className="sm:w-1/2">
            {product.featuredImageUrl ? (
              <img
                src={fixImgUrl(product.featuredImageUrl)}
                alt={displayTitle}
                className="w-full aspect-[3/4] object-cover"
              />
            ) : (
              <div className="w-full aspect-[3/4] bg-luxury-sand/30" />
            )}
          </div>

          {/* Info */}
          <div className="sm:w-1/2 p-5 flex flex-col justify-between">
            <div>
              <h3 className="text-sm font-medium tracking-wide mb-1">{displayTitle}</h3>
              <p className="text-sm text-luxury-charcoal mb-4">{formatStorefrontPrice(price, ccy)}</p>

              {/* Size selector pills */}
              {sizes.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-luxury-stone mb-2">{t("size", locale)}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sizes.map((s) => {
                      const inStock = isSizeInStock(s);
                      return (
                        <button
                          key={s}
                          onClick={() => onSelectSize(selectedSize === s ? null : s)}
                          disabled={!inStock}
                          className={`min-w-[2.5rem] h-8 px-2.5 text-xs border rounded transition-colors ${
                            selectedSize === s
                              ? "border-luxury-black bg-luxury-black text-white"
                              : inStock
                                ? "border-luxury-sand text-luxury-charcoal hover:border-luxury-charcoal"
                                : "border-luxury-sand/50 text-luxury-stone/40 line-through cursor-not-allowed"
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Add to Bag */}
            <button
              onClick={handleAddToBag}
              disabled={sizes.length > 0 && !selectedSize}
              className={`mt-5 w-full py-3 text-xs uppercase tracking-widest transition-colors rounded ${
                added
                  ? "bg-green-700 text-white"
                  : sizes.length > 0 && !selectedSize
                    ? "bg-luxury-sand text-luxury-stone cursor-not-allowed"
                    : "bg-luxury-black text-white hover:bg-luxury-charcoal"
              }`}
            >
              {added
                ? (locale === "en" ? "Added!" : "Da them!")
                : sizes.length > 0 && !selectedSize
                  ? t("selectSize", locale)
                  : t("addToBag", locale)}
            </button>

            {/* Link to full PDP */}
            <Link
              to={`/products/${product.handle || product.id}`}
              className="mt-2 text-center text-xs text-luxury-stone underline hover:text-luxury-charcoal"
              onClick={onClose}
            >
              {locale === "en" ? "View Full Details" : "Xem chi tiet"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pre-cutover hardening 2026-05-09: catch loader/action exceptions in
// the catalog page and render a graceful fallback instead of bubbling
// to the root error boundary (which 500s the entire shell). 119 other
// routes already had this; the catalog and checkout were the two
// high-traffic gaps surfaced in the launch audit.
export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
